import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

const ExpenseCategoryAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/expense-categories",
    token
  );

  const selectedBranch = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : auth?.user?.user?.selected_branch
      ? String(auth.user.user.selected_branch)
      : "";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [shop, setShop] = useState("");

  const { data: shopsData } = useFetchObjects(
    ["shops"],
    "shop/shop",
    token
  );
  const shops = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const hasSingleShop = shops.length === 1;

  useEffect(() => {
    if (shops.length === 0) return;

    const branchShop = selectedBranch
      ? shops.find((s) => String(s.id) === selectedBranch)
      : null;

    if (branchShop) {
      setShop(String(branchShop.id));
    } else if (hasSingleShop) {
      setShop(String(shops[0].id));
    } else if (!shop) {
      setShop(String(shops[0].id));
    }
  }, [hasSingleShop, selectedBranch, shop, shops]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("shop", shop);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setName("");
      setDescription("");
      if (selectedBranch) {
        setShop(selectedBranch);
      } else if (hasSingleShop) {
        setShop(String(shops[0].id));
      } else if (shops.length) {
        setShop(String(shops[0].id));
      } else {
        setShop("");
      }
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, selectedBranch, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("expenseCategory.addExpenseCategory")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            label={t("expenseCategory.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("expenseCategory.description")}
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("expenseCategory.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name || !shop}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t("expenseCategory.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExpenseCategoryAdd;
